"use client";

import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MuiLink from "@mui/material/Link";
import { useLocale } from "next-intl";

// Brand palette (matches email templates + MUI theme)
export const AUTH_COLORS = {
  teal: "#109bc5",
  tealDark: "#2D6F7A",
  coral: "#E55740",
  beige: "#F5F4F1",
  panel: "#DDEDF6",
  white: "#ffffff",
  textPrimary: "#1A1A1A",
  textSecondary: "#5A5A5A",
  border: "#E0E0E0",
};

// Warm printed-photo hero image from Unsplash (no auth required, public CDN)
const HERO_IMAGE_URL =
  "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?auto=format&fit=crop&w=900&q=80";

interface AuthLayoutProps {
  /** Tagline shown on the left hero panel */
  tagline: string;
  /** Sub-tagline / supporting text below the main tagline */
  subTagline?: string;
  children: React.ReactNode;
}

export function AuthLayout({ tagline, subTagline, children }: AuthLayoutProps) {
  const locale = useLocale();

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: AUTH_COLORS.beige,
      }}
    >
      {/* ── LEFT HERO PANEL (hidden on mobile) ───────────────────────────── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          width: { md: "42%", lg: "45%" },
          flexShrink: 0,
          flexDirection: "column",
          justifyContent: "flex-end",
          position: "relative",
          overflow: "hidden",
          // fallback background while image loads
          backgroundColor: AUTH_COLORS.tealDark,
        }}
      >
        {/* Background photo */}
        <Box
          component="img"
          src={HERO_IMAGE_URL}
          alt="Printed photos"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />

        {/* Dark gradient overlay so text is readable */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.04) 100%)",
          }}
        />

        {/* Brand mark at top-left */}
        <Box
          sx={{
            position: "absolute",
            top: 36,
            left: 40,
            zIndex: 2,
          }}
        >
          <MuiLink
            href={`/${locale}`}
            underline="none"
            aria-label="Go to home page"
          >
            <Box
              component="img"
              src="/images/logo/logo.png"
              alt="Foto Delavnica"
              sx={{ height: 36, display: "block", filter: "brightness(0) invert(1)" }}
            />
          </MuiLink>
        </Box>

        {/* Tagline at bottom */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            p: { md: "40px", lg: "52px" },
          }}
        >
          <Typography
            variant="h3"
            component="p"
            sx={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontWeight: 400,
              color: "#ffffff",
              lineHeight: 1.25,
              fontSize: { md: "1.75rem", lg: "2.1rem" },
              mb: subTagline ? 1.5 : 0,
            }}
          >
            {tagline}
          </Typography>
          {subTagline && (
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.72)",
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontStyle: "italic",
                fontSize: "1rem",
              }}
            >
              {subTagline}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── RIGHT FORM PANEL ─────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 3, sm: 5, md: 6 },
          py: { xs: 6, md: 8 },
          backgroundColor: AUTH_COLORS.white,
          overflowY: "auto",
        }}
      >
        {/* Logo — visible on mobile (left panel hidden) */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            mb: 4,
            textAlign: "center",
          }}
        >
          <MuiLink href={`/${locale}`} underline="none" aria-label="Go to home page">
            <Box
              component="img"
              src="/images/logo/logo.png"
              alt="Foto Delavnica"
              sx={{ height: 30, display: "inline-block" }}
            />
          </MuiLink>
        </Box>

        {/* Form card container */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

// ── Reusable styled sub-components ───────────────────────────────────────────

/** Page heading inside the form panel */
export function AuthHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="h4"
      component="h1"
      sx={{
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontWeight: 400,
        fontSize: { xs: "1.6rem", sm: "1.85rem" },
        color: AUTH_COLORS.textPrimary,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

/** Supporting subtitle under the heading */
export function AuthSubtitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="body1"
      sx={{
        color: AUTH_COLORS.textSecondary,
        mb: 4,
        lineHeight: 1.6,
      }}
    >
      {children}
    </Typography>
  );
}
